import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { VehicleDetails } from '../../types';
import { vehicleContext } from '../VehicleProvider';
import FallbackCarDefault from '../../assets/images/fallback_car_default.png';
import {
  Details,
  DetailsItem,
  DetailsList,
  DetailsListWrap,
  HeaderWrap,
  ImageLegal,
  LegalDisclaimerIcon,
  LegalDisclaimerWrap,
  VehicleImage,
  VehicleImageWrap,
  Wrapper,
} from './StandardEquipmentContent.style';
import { Headline, Icon, Layout, Text } from '@oneaudi/unified-web-components';
import { SYS_SIZE_FIXED_150 } from '@oneaudi/unified-web-common';

const StandardEquipmentContent: React.FC = () => {
  const { vehicle, market } = useContext(vehicleContext);
  const { t } = useTranslation();

  const { vehicleMedia, equipments, trimline, trimName, modelName } = vehicle as VehicleDetails;
  const { standardEquipments } = equipments;
  const { assistanceSystems, exterior, interior, performance, technology, trimAndPackages } =
    standardEquipments;

  if (!vehicle) return null;

  const { mediaImages } = vehicleMedia;

  const vehicleImage =
    mediaImages?.find((image) => image.imageType === 'front_facing')?.url || FallbackCarDefault;
  const isCAN = market === 'ca' || market === 'cauc';
  const isUS = market === 'us' || market === 'usuc';

  const listStyle = {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
  };

  return (
    <Wrapper data-testid="standard-equipment-wrapper">
      <Details>
        <HeaderWrap>
          {trimline && (
            <Headline
              tag="h1"
              variant={{
                order: '3',
                type: 'headline',
                weight: 'normal',
              }}
              data-testid="trim-line"
            >
              {trimline}
            </Headline>
          )}
          {trimName && (
            <Headline
              tag="h1"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'normal',
              }}
              data-testid="trim-name"
            >
              {trimName}
            </Headline>
          )}
          <LegalDisclaimerWrap>
            <LegalDisclaimerIcon>
              <Icon name="info-circle" size="s" />
            </LegalDisclaimerIcon>
            <Text margin={[0, 0, 0, 0]} data-testid="legal-disclaimer">
              {isCAN
                ? t('featuresAndSpecsSection.categories.standardEquipment.disclaimers.disclaimerCA')
                : isUS
                  ? t(
                      'featuresAndSpecsSection.categories.standardEquipment.disclaimers.disclaimerUS',
                    )
                  : ''}
            </Text>
          </LegalDisclaimerWrap>
        </HeaderWrap>
        <DetailsListWrap>
          <DetailsList data-testid="equipment-category-list">
            {!!assistanceSystems?.length && (
              <DetailsItem data-testid="assistance-systems">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t(
                    'featuresAndSpecsSection.categories.standardEquipment.sectionTitles.assistanceSystems',
                  )}
                </Text>
                <ul style={listStyle}>
                  {assistanceSystems.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </DetailsItem>
            )}
            {!!exterior?.length && (
              <DetailsItem data-testid="exterior">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t('featuresAndSpecsSection.categories.standardEquipment.sectionTitles.exterior')}
                </Text>
                <ul style={listStyle}>
                  {exterior.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </DetailsItem>
            )}
            {!!interior?.length && (
              <DetailsItem data-testid="interior">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t('featuresAndSpecsSection.categories.standardEquipment.sectionTitles.interior')}
                </Text>
                <ul style={listStyle}>
                  {interior.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </DetailsItem>
            )}
          </DetailsList>

          <Layout>
            {!!performance?.length && (
              <Layout direction={'column'} data-testid="performance">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t(
                    'featuresAndSpecsSection.categories.standardEquipment.sectionTitles.performance',
                  )}
                </Text>
                <ul style={listStyle}>
                  {performance.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Layout>
            )}
            {!!technology?.length && (
              <Layout direction={'column'} data-testid="technology">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t(
                    'featuresAndSpecsSection.categories.standardEquipment.sectionTitles.technology',
                  )}
                </Text>
                <ul style={listStyle}>
                  {technology.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Layout>
            )}
            {!!trimAndPackages?.length && (
              <Layout direction={'column'} data-testid="trim-and-packages">
                <Text
                  tag="p"
                  variant={{
                    order: '2',
                    type: 'copy',
                    style: 'bold',
                  }}
                  margin={[0, 0, SYS_SIZE_FIXED_150, 0]}
                >
                  {t(
                    'featuresAndSpecsSection.categories.standardEquipment.sectionTitles.trimsAndPackages',
                  )}
                </Text>
                <ul style={listStyle}>
                  {trimAndPackages.map((item, index) => (
                    <li key={`${item.headline}${index}`}>
                      <Text
                        variant={{
                          order: '2',
                          type: 'copy',
                          style: 'normal',
                        }}
                      >
                        {item.headline}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Layout>
            )}
          </Layout>
        </DetailsListWrap>
      </Details>
      {vehicleImage && (
        <ImageLegal>
          <VehicleImageWrap>
            <VehicleImage src={vehicleImage} alt={modelName} data-testid="vehicle-image" />
          </VehicleImageWrap>
        </ImageLegal>
      )}
    </Wrapper>
  );
};

export default StandardEquipmentContent;
